// Pixi lightning arcs from a Lightning card toward matching dice (visual only).
import { Container, Graphics, Text } from 'pixi.js';
import { formatLightningMultiplierTenths } from '../game/lightning-types.js';

function sleep(ms: number): Promise<void> {
  return new Promise((r) => window.setTimeout(r, ms));
}

export class LightningArcLayer extends Container {
  private readonly gfx = new Graphics();
  private readonly labels = new Container();
  private generation = 0;

  constructor() {
    super();
    this.addChild(this.gfx, this.labels);
    this.eventMode = 'none';
  }

  cancel(): void {
    this.generation += 1;
    this.gfx.clear();
    this.labels.removeChildren();
  }

  async playArcs(
    from: { x: number; y: number },
    targets: Array<{ x: number; y: number }>,
    multiplierTenths: number,
    reducedMotion: boolean,
  ): Promise<void> {
    const gen = ++this.generation;
    this.gfx.clear();
    this.labels.removeChildren();
    if (targets.length === 0) return;

    const drawBolt = (x0: number, y0: number, x1: number, y1: number, alpha: number): void => {
      const segs = reducedMotion ? 3 : 6;
      let px = x0;
      let py = y0;
      this.gfx.moveTo(px, py);
      for (let i = 1; i <= segs; i++) {
        const t = i / segs;
        const x = x0 + (x1 - x0) * t + (Math.random() - 0.5) * (reducedMotion ? 4 : 12);
        const y = y0 + (y1 - y0) * t + (Math.random() - 0.5) * (reducedMotion ? 3 : 8);
        this.gfx.lineTo(x, y);
        px = x;
        py = y;
      }
      this.gfx.stroke({ width: reducedMotion ? 2 : 3, color: 0x6ee2ff, alpha });
      this.gfx.moveTo(x0, y0);
      for (let i = 1; i <= segs; i++) {
        const t = i / segs;
        const x = x0 + (x1 - x0) * t + (Math.random() - 0.5) * 6;
        const y = y0 + (y1 - y0) * t + (Math.random() - 0.5) * 4;
        this.gfx.lineTo(x, y);
      }
      this.gfx.stroke({ width: 1, color: 0xffffff, alpha: alpha * 0.85 });
    };

    for (const target of targets) {
      if (gen !== this.generation) return;
      drawBolt(from.x, from.y, target.x, target.y, reducedMotion ? 0.7 : 1);
      const label = new Text({
        text: formatLightningMultiplierTenths(multiplierTenths),
        style: { fontSize: 11, fill: 0x6ee2ff, fontWeight: '700' },
      });
      label.anchor.set(0.5, 1);
      label.position.set(target.x, target.y - 28);
      this.labels.addChild(label);
      await sleep(reducedMotion ? 120 : 220);
    }
    await sleep(reducedMotion ? 180 : 320);
    if (gen === this.generation) {
      this.gfx.clear();
      this.labels.removeChildren();
    }
  }
}
